import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('wholesaleToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem('wholesaleToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ── Auth ──────────────────────────────────────────────────────────────────────
export const auth = {
  login: (data)  => api.post('/api/auth/login', data),
  me:    ()      => api.get('/api/auth/me'),
};

// ── Admin — Customers ─────────────────────────────────────────────────────────
export const adminCustomers = {
  list:           (params)                    => api.get('/api/admin/customers', { params }),
  reps:           ()                          => api.get('/api/admin/customers/reps'),
  bulk:           (data)                      => api.post('/api/admin/customers/bulk', data),
  get:            (id)                        => api.get(`/api/admin/customers/${id}`),
  create:         (data)                      => api.post('/api/admin/customers', data),
  update:         (id, data)                  => api.put(`/api/admin/customers/${id}`, data),
  getOverrides:       (id)                    => api.get(`/api/admin/customers/${id}/overrides`),
  createOverride:     (id, data)              => api.post(`/api/admin/customers/${id}/overrides`, data),
  updateOverride:     (id, overrideId, data)  => api.put(`/api/admin/customers/${id}/overrides/${overrideId}`, data),
  deleteOverride:     (id, overrideId)        => api.delete(`/api/admin/customers/${id}/overrides/${overrideId}`),
  updatePriceLevel:   (id, data)              => api.put(`/api/admin/customers/${id}`, data),
  getPricingHistory:  (id)                    => api.get(`/api/admin/customers/${id}/pricing-history`),
  impersonate:        (id)                    => api.post(`/api/admin/customers/${id}/impersonate`),
  getMessages:        (id)                    => api.get(`/api/admin/customers/${id}/messages`),
  updateContact:      (id, data)              => api.put(`/api/admin/customers/${id}/contact`, data),
};

// ── Admin — Orders ────────────────────────────────────────────────────────────
export const adminOrders = {
  list:          (params) => api.get('/api/admin/orders', { params }),
  export:        (params) => api.get('/api/admin/orders/export', { params, responseType: 'blob' }),
  create:        (data)   => api.post('/api/admin/orders/create', data),
  get:           (id)     => api.get(`/api/admin/orders/${id}`),
  updateStatus:  (id, data) => api.put(`/api/admin/orders/${id}/status`, data),
  updateTracking:(id, data) => api.put(`/api/admin/orders/${id}/tracking`, data),
  updateNotes:   (id, data) => api.put(`/api/admin/orders/${id}/notes`, data),
  getInvoice:    (id)     => api.get(`/api/admin/orders/${id}/invoice`),
  sendInvoice:   (id, data) => api.post(`/api/admin/orders/${id}/send-invoice`, data || {}),
  sendReminder:  (id, data) => api.post(`/api/admin/orders/${id}/send-reminder`, data),
  getEmailLog:   (id)     => api.get(`/api/admin/orders/${id}/email-log`),
  getPayments:   (id)           => api.get(`/api/admin/orders/${id}/payments`),
  recordPayment: (id, data)     => api.post(`/api/admin/orders/${id}/payments`, data),
  deletePayment: (id, pmtId)    => api.delete(`/api/admin/orders/${id}/payments/${pmtId}`),
  cancelOrder:     (id, data)  => api.put(`/api/admin/orders/${id}/cancel`, data),
  editItems:       (id, data)  => api.put(`/api/admin/orders/${id}/items`, data),
  issueRefund:     (id, data)  => api.post(`/api/admin/orders/${id}/refund`, data),
  updatePickupStatus: (id, pickup_status) => api.patch(`/api/admin/orders/${id}/pickup-status`, { pickup_status }),
};

// ── Admin — Products ──────────────────────────────────────────────────────────
export const adminProducts = {
  list:                (params)           => api.get('/api/admin/products', { params }),
  get:                 (id)               => api.get(`/api/admin/products/${id}`),
  update:              (id, data)         => api.put(`/api/admin/products/${id}`, data),
  getTiers:            (id)               => api.get(`/api/admin/products/${id}/tiers`),
  getCustomerOverrides:(id)               => api.get(`/api/admin/products/${id}/customer-overrides`),
  createTier:          (id, data)         => api.post(`/api/admin/products/${id}/tiers`, data),
  updateTier:          (id, tierId, data) => api.put(`/api/admin/products/${id}/tiers/${tierId}`, data),
  deleteTier:          (id, tierId)       => api.delete(`/api/admin/products/${id}/tiers/${tierId}`),
  getFeatured:         ()                 => api.get('/api/admin/products/featured'),
  updateFeatured:      (id, data)         => api.put(`/api/admin/products/${id}/featured`, data),
  syncC7:              (id)               => api.post(`/api/admin/products/${id}/sync-c7`),
  previewC7:           (id)               => api.get(`/api/admin/products/${id}/c7-preview`),
  syncC7Bulk:          ()                 => api.post('/api/admin/products/sync-c7-bulk'),
};

// ── Admin — Settings ──────────────────────────────────────────────────────────
export const adminSettings = {
  getUsers:        ()          => api.get('/api/admin/settings/users'),
  createUser:      (data)      => api.post('/api/admin/settings/users', data),
  updateUser:      (id, data)  => api.put(`/api/admin/settings/users/${id}`, data),
  deactivateUser:  (id)        => api.post(`/api/admin/settings/users/${id}/deactivate`),
  resetPassword:   (id)        => api.post(`/api/admin/settings/users/${id}/reset-password`),
  getReps:         ()          => api.get('/api/admin/settings/reps'),
  updateRepPhone:          (id, phone)          => api.put(`/api/admin/settings/reps/${id}/phone`, { phone }),
  updateRepCommissionRate: (id, commissionRate) => api.put(`/api/admin/settings/reps/${id}/commission-rate`, { commissionRate }),
  getSystem:       ()          => api.get('/api/admin/settings/system'),
  updateSystem:    (data)      => api.put('/api/admin/settings/system', data),
  testResend:      (data)      => api.post('/api/admin/settings/resend/test', data || {}),
};

// ── Admin — Commissions ───────────────────────────────────────────────────────
export const adminCommissions = {
  list:             (params) => api.get('/api/admin/commissions', { params }),
  summary:          (params) => api.get('/api/admin/commissions/summary', { params }),
  incentivePayouts: (params) => api.get('/api/admin/commissions/incentive-payouts', { params }),
  disbursements:    (params) => api.get('/api/admin/commissions/disbursements', { params }),
  disburse:         (body)   => api.post('/api/admin/commissions/disburse', body),
};

// ── Admin — Reports ───────────────────────────────────────────────────────────
export const adminReports = {
  summary:     () => api.get('/api/admin/reports/summary'),
  revenue:     (params) => api.get('/api/admin/reports/revenue', { params }),
  topProducts: (params) => api.get('/api/admin/reports/top-products', { params }),
  aging:       () => api.get('/api/admin/reports/aging'),
  salesByRep:  () => api.get('/api/admin/reports/sales-by-rep'),
};

// ── Wholesale (customer-facing) ───────────────────────────────────────────────
export const wholesaleAuth = {
  register: (formData) => api.post('/api/wholesale/auth/register', formData),
};

export const wholesaleOrders = {
  calculate:         (data) => api.post('/api/wholesale/orders/calculate', data),
  create:            (data) => api.post('/api/wholesale/orders', data),
  list:              ()     => api.get('/api/wholesale/orders'),
  get:               (id)   => api.get(`/api/wholesale/orders/${id}`),
  getInvoice:        (id)   => api.get(`/api/wholesale/orders/${id}/invoice`),
  checkReorder:      (id)   => api.get(`/api/wholesale/orders/${id}/reorder-check`),
  getLastOrderItems: ()     => api.get('/api/wholesale/orders/last-items'),
};

export const wholesaleProducts = {
  list: (params) => api.get('/api/wholesale/products', { params }),
  get:  (id)     => api.get(`/api/wholesale/products/${id}`),
};

export const wholesaleCustomers = {
  getProfile:          ()     => api.get('/api/wholesale/customers/profile'),
  updateProfile:       (data) => api.put('/api/wholesale/customers/profile', data),
  getPaymentMethods:   ()     => api.get('/api/wholesale/customers/payment-methods'),
  addPaymentMethod:    (data) => api.post('/api/wholesale/customers/payment-methods', data),
  setDefaultPayment:   (pmId) => api.post(`/api/wholesale/customers/payment-methods/${pmId}/default`),
  deletePaymentMethod: (pmId) => api.delete(`/api/wholesale/customers/payment-methods/${pmId}`),
  getAnnouncement:     ()     => api.get('/api/wholesale/customers/announcement'),
  sendMessage:         (data) => api.post('/api/wholesale/customers/message', data),
};

// ── Admin — Messages ──────────────────────────────────────────────────────────
export const adminMessages = {
  getUnreadCount: () => api.get('/api/admin/messages/unread-count'),
};

// ── Agent — Tickets ───────────────────────────────────────────────────────────
export const agentTickets = {
  list:             (params)                        => api.get('/api/agent/tickets', { params }),
  get:              (ticketID)                      => api.get(`/api/agent/tickets/${ticketID}`),
  assign:           (ticketID, repID)               => api.patch(`/api/agent/tickets/${ticketID}/assign`, { repID }),
  updateStatus:     (ticketID, status)              => api.patch(`/api/agent/tickets/${ticketID}/status`, { status }),
  approve:          (ticketID, finalBody, actionType) => api.post(`/api/agent/tickets/${ticketID}/approve`, { finalBody, actionType }),
  reject:           (ticketID, reason, rejectionCategory) => api.post(`/api/agent/tickets/${ticketID}/reject`, { reason, rejectionCategory }),
  escalate:         (ticketID, reason)              => api.post(`/api/agent/tickets/${ticketID}/escalate`, { reason }),
  pushToGmailDraft: (ticketID, finalBody)           => api.post(`/api/agent/tickets/${ticketID}/push-to-draft`, { finalBody }),
  stats:            ()                              => api.get('/api/agent/tickets/stats'),
  bulk:             (action, ticketIDs)             => api.post('/api/agent/tickets/bulk', { action, ticketIDs }),
  setEscalation:    (ticketID, escalationLevel)     => api.patch(`/api/agent/tickets/${ticketID}/escalation`, { escalationLevel }),
  lock:             (ticketID)                      => api.post(`/api/agent/tickets/${ticketID}/lock`),
  unlock:           (ticketID)                      => api.delete(`/api/agent/tickets/${ticketID}/lock`),
  close:            (ticketID)                      => api.patch(`/api/agent/tickets/${ticketID}/close`),
  reopen:           (ticketID)                      => api.patch(`/api/agent/tickets/${ticketID}/reopen`),
  snooze:           (ticketID, until)               => api.post(`/api/agent/tickets/${ticketID}/snooze`, { until }),
  unsnooze:         (ticketID)                      => api.delete(`/api/agent/tickets/${ticketID}/snooze`),
  getNotes:         (ticketID)                      => api.get(`/api/agent/tickets/${ticketID}/notes`),
  createNote:       (ticketID, noteText)            => api.post(`/api/agent/tickets/${ticketID}/notes`, { noteText }),
  deleteNote:       (ticketID, noteID)              => api.delete(`/api/agent/tickets/${ticketID}/notes/${noteID}`),
  getThread:        (ticketID)                      => api.get(`/api/agent/tickets/${ticketID}/thread`),
  regenerateDraft:  (ticketID, hint)                => api.post(`/api/agent/tickets/${ticketID}/regenerate-draft`, { hint: hint || undefined }),
  cancelSend:       (ticketID)                      => api.post(`/api/agent/tickets/${ticketID}/cancel-send`),
  createManual:     (payload)                       => api.post('/api/agent/tickets/manual', payload),
  getTemplates:     ()                              => api.get('/api/agent/tickets/templates'),
  updateTemplates:  (templates)                     => api.put('/api/agent/tickets/templates', { templates }),
};

// ── Agent — Gmail ─────────────────────────────────────────────────────────────
export const agentGmail = {
  status:      ()     => api.get('/api/agent/gmail/status'),
  authUrl:     ()     => api.get('/api/agent/gmail/auth-url'),
  watch:       ()     => api.post('/api/agent/gmail/watch'),
  stopWatches: ()     => api.delete('/api/agent/gmail/watch'),
  poll:        ()     => api.get('/api/agent/gmail/poll'),
  disconnect:  ()     => api.delete('/api/agent/gmail/disconnect'),
};

// ── Agent — Monitoring ────────────────────────────────────────────────────────
export const agentMonitoring = {
  stats:           ()        => api.get('/api/agent/monitoring/stats'),
  costs:           ()        => api.get('/api/agent/monitoring/costs'),
  health:          ()        => api.get('/api/agent/monitoring/health'),
  feedback:        ()        => api.get('/api/agent/monitoring/feedback'),
  getSettings:     ()        => api.get('/api/agent/monitoring/settings'),
  updateSettings:  (payload) => api.put('/api/agent/monitoring/settings', payload),
};

// ── Admin — Account Notes (CRM) ───────────────────────────────────────────────
export const accountNotes = {
  list:   (customerID)              => api.get(`/api/admin/customers/${customerID}/notes`),
  create: (customerID, body)        => api.post(`/api/admin/customers/${customerID}/notes`, body),
  update: (customerID, noteID, body) => api.patch(`/api/admin/customers/${customerID}/notes/${noteID}`, body),
  delete: (customerID, noteID)      => api.delete(`/api/admin/customers/${customerID}/notes/${noteID}`),
};

// ── Admin — Follow-Ups (CRM) ──────────────────────────────────────────────────
export const followUps = {
  listForCustomer: (customerID)        => api.get(`/api/admin/customers/${customerID}/follow-ups`),
  create:          (customerID, body)  => api.post(`/api/admin/customers/${customerID}/follow-ups`, body),
  update:          (customerID, id, body) => api.patch(`/api/admin/customers/${customerID}/follow-ups/${id}`, body),
  delete:          (customerID, id)    => api.delete(`/api/admin/customers/${customerID}/follow-ups/${id}`),
  listAll:         (params)            => api.get('/api/admin/follow-ups', { params }),
  dueToday:        ()                  => api.get('/api/admin/follow-ups/due-today'),
};

// ── Admin — Collections (SF-1) ────────────────────────────────────────────────
export const adminCollections = {
  list:    ()         => api.get('/api/admin/collections'),
  create:  (data)     => api.post('/api/admin/collections', data),
  update:  (id, data) => api.put(`/api/admin/collections/${id}`, data),
  remove:  (id)       => api.delete(`/api/admin/collections/${id}`),
  reorder: (items)    => api.post('/api/admin/collections/reorder', items),
};

// ── Admin — Uploads (SF-1 / SF-3) ────────────────────────────────────────────
export const adminUploads = {
  // Product images — tied to a specific productID, updates proLabelImageUrl in DB
  uploadProductImage: (productId, formData) => api.post(`/api/admin/uploads/products/${productId}`, formData),
  deleteProductImage: (productId)           => api.delete(`/api/admin/uploads/products/${productId}`),

  // Banner image — returns URL only; caller persists via adminSettings.updateSystem
  uploadBannerImage: (fd) => api.post('/api/admin/uploads/banner-image', fd,
    { headers: { 'Content-Type': 'multipart/form-data' } }),

  // Collection cover image — returns URL only; caller embeds in collection create/update body
  uploadCollectionImage: (fd) => api.post('/api/admin/uploads/collection-image', fd,
    { headers: { 'Content-Type': 'multipart/form-data' } }),

  // Invoice logo — stores URL in tblSystemSettings under invoice_logo_url
  uploadLogoImage: (fd) => api.post('/api/admin/uploads/logo', fd,
    { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteLogoImage: ()   => api.delete('/api/admin/uploads/logo'),
};

// ── Admin — Addresses (multi-location) ────────────────────────────────────────
export const adminAddresses = {
  list:       (customerID)                  => api.get(`/api/admin/customers/${customerID}/addresses`),
  create:     (customerID, data)            => api.post(`/api/admin/customers/${customerID}/addresses`, data),
  update:     (customerID, addressID, data) => api.put(`/api/admin/customers/${customerID}/addresses/${addressID}`, data),
  delete:     (customerID, addressID)       => api.delete(`/api/admin/customers/${customerID}/addresses/${addressID}`),
  setPrimary: (customerID, addressID)       => api.put(`/api/admin/customers/${customerID}/addresses/${addressID}/set-primary`),
};

// ── Admin — Contact Emails ────────────────────────────────────────────────────
export const adminEmails = {
  list:       (customerID)                => api.get(`/api/admin/customers/${customerID}/emails`),
  create:     (customerID, data)          => api.post(`/api/admin/customers/${customerID}/emails`, data),
  update:     (customerID, emailID, data) => api.put(`/api/admin/customers/${customerID}/emails/${emailID}`, data),
  delete:     (customerID, emailID)       => api.delete(`/api/admin/customers/${customerID}/emails/${emailID}`),
  setPrimary: (customerID, emailID)       => api.put(`/api/admin/customers/${customerID}/emails/${emailID}/set-primary`),
};

// ── Admin — Shipping (GLS) ────────────────────────────────────────────────────
export const adminShipping = {
  getPickupLocations: ()           => api.get('/api/admin/shipping/pickup-locations'),
  createLabels:       (id, data)   => api.post(`/api/admin/shipping/${id}/create-labels`, data),
  getLabels:          (id)         => api.get(`/api/admin/shipping/${id}/labels`),
  emailLabels:        (id, data)   => api.post(`/api/admin/shipping/${id}/email-labels`, data),
  requestPickup:      (id, data)   => api.post(`/api/admin/shipping/${id}/request-pickup`, data),
};

// ── Admin — Companies ─────────────────────────────────────────────────────────
export const adminCompanies = {
  updateQboCustomerId: (companyId, qboCustomerId) =>
    api.patch(`/api/admin/companies/${companyId}/qbo-customer`, { qboCustomerId }),
  updateQboAutocreate: (companyId, qboAutocreate) =>
    api.patch(`/api/admin/companies/${companyId}/qbo-autocreate`, { qboAutocreate }),
};

// ── Admin — QBO ───────────────────────────────────────────────────────────────
export const adminQbo = {
  getOrders:        (params)          => api.get('/api/qbo/orders', { params }),
  retryOrder:       (orderId)         => api.post(`/api/qbo/retry/${orderId}`),
  updateInvoice:    (orderId)         => api.post(`/api/qbo/update-invoice/${orderId}`),
  retryPayment:     (payId)           => api.post(`/api/qbo/retry-payment/${payId}`),
  searchCustomer:   (name, email)     => api.get('/api/qbo/search-customer', { params: { name, email } }),
};

// ── Wholesale — Home (SF-1) ───────────────────────────────────────────────────
export const wholesaleHome = {
  get: () => api.get('/api/wholesale/home'),
};

// ── Wholesale — Cart persistence ──────────────────────────────────────────────
export const wholesaleCart = {
  get:   ()       => api.get('/api/wholesale/cart'),
  set:   (items)  => api.put('/api/wholesale/cart', { items }),
  clear: ()       => api.delete('/api/wholesale/cart'),
};

// ── Wholesale — Pickup Locations ──────────────────────────────────────────────
export const wholesalePickupLocations = {
  list: () => api.get('/api/wholesale/pickup-locations'),
};

// ── Wholesale — Shipping (rules engine) ───────────────────────────────────────
export const wholesaleShipping = {
  calculate: (cart) => api.post('/api/wholesale/shipping/calculate', { cart }),
};

// ── Admin — Pickup Locations (CRUD) ───────────────────────────────────────────
export const adminPickupLocations = {
  list:   ()         => api.get('/api/admin/pickup-locations'),
  create: (data)     => api.post('/api/admin/pickup-locations', data),
  update: (id, data) => api.patch(`/api/admin/pickup-locations/${id}`, data),
  remove: (id)       => api.delete(`/api/admin/pickup-locations/${id}`),
};

// ── Admin — Shipping Rules (CRUD) ─────────────────────────────────────────────
export const adminShippingRules = {
  list:   ()         => api.get('/api/admin/shipping-rules'),
  create: (data)     => api.post('/api/admin/shipping-rules', data),
  update: (id, data) => api.patch(`/api/admin/shipping-rules/${id}`, data),
  remove: (id)       => api.delete(`/api/admin/shipping-rules/${id}`),
};

export default api;
